import React, { useEffect, useState, useMemo } from 'react';
import {
    Popper,
    Paper,
    Container,
    Grid,
    Fade,
    ClickAwayListener,
    Box,
    Typography,
    Skeleton,
    Alert,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { MegaMenuCard } from './mega-menu-card';
import { useMenusData } from '../../../hooks/useMenusData';
import { IMenuResponse } from '../../../model/base/response/menu-response';

interface MegaMenuDropdownProps {
    open: boolean;
    onClose: () => void;
    anchorEl: HTMLElement | null;
}

export const MegaMenuDropdown: React.FC<MegaMenuDropdownProps> = ({ open, onClose, anchorEl }) => {
    const { menus, loading } = useMenusData();
    const [searchTerm, setSearchTerm] = useState('');

    // Filtrar menus baseado no termo de busca
    const filteredMenus = useMemo(() => {
        if (!menus || !searchTerm.trim()) return menus;

        const lowerSearch = searchTerm.toLowerCase().trim();

        return menus
            .map((menu: IMenuResponse) => {
                const menuNameMatch = menu.name?.toLowerCase().includes(lowerSearch);
                const menuDescMatch = menu.description?.toLowerCase().includes(lowerSearch);

                const filteredItems = menu.items?.filter(item =>
                    item.name?.toLowerCase().includes(lowerSearch)
                );

                if (menuNameMatch || menuDescMatch) {
                    return menu;
                }

                if (filteredItems && filteredItems.length > 0) {
                    return {
                        ...menu,
                        items: filteredItems
                    };
                }

                return null;
            })
            .filter((menu): menu is IMenuResponse => menu !== null);
    }, [menus, searchTerm]);

    // Limpar busca ao fechar
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
        }
    }, [open]);

    // Fechar com ESC
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && open) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, onClose]);

    if (!anchorEl) return null;

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{
                width: '100vw',
                zIndex: 1300,
                left: '0 !important',
                transform: 'none !important',
            }}
            transition
            disablePortal={false}
            modifiers={[
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8],
                    },
                },
            ]}
        >
            {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                    <Paper
                        elevation={8}
                        role="dialog"
                        aria-label="Mais opções"
                        sx={{
                            width: '100vw',
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            borderRadius: 0,
                            backgroundColor: 'background.subtle',
                            borderTop: 3,
                            borderColor: 'primary.main',
                        }}
                    >
                        <ClickAwayListener onClickAway={onClose}>
                            <Container maxWidth="xl" sx={{ py: 4 }}>
                                {/* Cabeçalho com Título e Busca */}
                                <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h3" component="h2" gutterBottom>
                                            Mais opções
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Explore todos os serviços e funcionalidades do portal
                                        </Typography>
                                    </Box>
                                    <TextField
                                        placeholder="Buscar por..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        size="small"
                                        inputProps={{ 'aria-label': 'Filtrar opções' }}
                                        sx={{
                                            minWidth: { xs: '100%', sm: 250, md: 300 },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {/* Loading State */}
                                {loading && (
                                    <Grid container spacing={3}>
                                        {[1, 2, 3, 4].map((item) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                                                <Skeleton variant="rounded" height={250} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {/* Empty State */}
                                {!loading && (!menus || menus.length === 0) && (
                                    <Alert severity="info">
                                        Nenhum menu disponível no momento.
                                    </Alert>
                                )}

                                {/* No Results State */}
                                {!loading && menus && menus.length > 0 && filteredMenus.length === 0 && searchTerm.trim() && (
                                    <Alert severity="warning">
                                        Nenhum resultado encontrado para "{searchTerm}". Tente buscar por outros termos.
                                    </Alert>
                                )}

                                {/* Menu Grid */}
                                {!loading && filteredMenus && filteredMenus.length > 0 && (
                                    <Grid container spacing={3}>
                                        {filteredMenus.map((menu) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={menu.id}>
                                                <MegaMenuCard menu={menu} onNavigate={onClose} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}

                                {/* Footer Info */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        pt: 2,
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        Pressione <strong>ESC</strong> ou clique fora para fechar
                                    </Typography>
                                </Box>
                            </Container>
                        </ClickAwayListener>
                    </Paper>
                </Fade>
            )}
        </Popper>
    );
};
